import React from 'react';
import { useSelector } from 'react-redux';
import { selectDarkMode } from '../../../app/store/slices/uiSlice';

/**
 * Loader Component
 * A reusable loading spinner component with multiple variants
 *
 * @param {string} variant - Loader variant (spinner/dots/pulse/bars/gradient)
 * @param {string} size - Loader size (sm/md/lg/xl)
 * @param {string} color - Loader color (primary/white/gray/gradient)
 * @param {string} text - Optional loading text
 * @param {boolean} fullScreen - Show as full screen overlay
 * @param {string} className - Additional CSS classes
 */
const Loader = ({
  variant = 'spinner',
  size = 'md',
  color = 'primary',
  text,
  fullScreen = false,
  className = '',
}) => {
  const darkMode = useSelector(selectDarkMode);

  const sizeClasses = {
    sm: 'h-4 w-4',
    md: 'h-8 w-8',
    lg: 'h-12 w-12',
    xl: 'h-16 w-16',
  };

  const borderSizes = {
    sm: 'border-2',
    md: 'border-2',
    lg: 'border-3',
    xl: 'border-4',
  };

  const colorClasses = {
    primary: 'border-blue-600 border-t-transparent',
    white: 'border-white border-t-transparent',
    gray: darkMode ? 'border-gray-400 border-t-transparent' : 'border-gray-600 border-t-transparent',
    gradient: 'border-transparent',
  };

  const dotSizes = {
    sm: 'w-1.5 h-1.5',
    md: 'w-2.5 h-2.5',
    lg: 'w-3.5 h-3.5',
    xl: 'w-5 h-5',
  };

  const dotColors = {
    primary: 'bg-blue-600',
    white: 'bg-white',
    gray: darkMode ? 'bg-gray-400' : 'bg-gray-600',
    gradient: 'bg-gradient-to-r from-blue-500 to-purple-500',
  };

  // Spinner variant
  const SpinnerLoader = () => (
    <div
      className={`
        animate-spin rounded-full
        ${sizeClasses[size]}
        ${borderSizes[size]}
        ${colorClasses[color]}
        ${color === 'gradient' ? 'bg-gradient-to-r from-blue-500 to-purple-500 mask-spinner' : ''}
        ${className}
      `}
    />
  );

  // Gradient spinner variant
  const GradientSpinner = () => (
    <div className={`relative ${sizeClasses[size]}`}>
      <div
        className={`
          absolute inset-0 rounded-full animate-spin
          bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500
        `}
      />
      <div
        className={`
          absolute inset-1 rounded-full
          ${darkMode ? 'bg-gray-800' : 'bg-white'}
        `}
      />
    </div>
  );

  // Bouncing dots variant
  const DotsLoader = () => (
    <div className="flex items-center space-x-1.5">
      {[0, 1, 2].map((index) => (
        <div
          key={index}
          className={`
            ${dotSizes[size]}
            ${dotColors[color]}
            rounded-full animate-bounce
          `}
          style={{ animationDelay: `${index * 150}ms` }}
        />
      ))}
    </div>
  );

  // Pulsing dots variant
  const PulseDotsLoader = () => (
    <div className="flex items-center space-x-2">
      {[0, 1, 2].map((index) => (
        <div
          key={index}
          className={`
            ${dotSizes[size]}
            ${dotColors[color]}
            rounded-full animate-pulse-dot
          `}
          style={{ animationDelay: `${index * 200}ms` }}
        />
      ))}
    </div>
  );

  // Bars loader variant
  const BarsLoader = () => {
    const barHeights = {
      sm: 'h-4',
      md: 'h-6',
      lg: 'h-8',
      xl: 'h-10',
    };

    return (
      <div className="flex items-end space-x-1">
        {[0, 1, 2, 3, 4].map((index) => (
          <div
            key={index}
            className={`
              w-1 ${barHeights[size]}
              ${dotColors[color]}
              rounded-full
              animate-pulse
              origin-bottom
            `}
            style={{
              animationDelay: `${index * 100}ms`,
              transform: `scaleY(${0.4 + (index % 3) * 0.2})`,
            }}
          />
        ))}
      </div>
    );
  };

  // Ring loader variant
  const RingLoader = () => (
    <div className={`relative ${sizeClasses[size]}`}>
      <div
        className={`
          absolute inset-0 rounded-full border-2
          ${darkMode ? 'border-gray-600' : 'border-blue-200'}
        `}
      />
      <div
        className={`
          absolute inset-0 rounded-full border-2 border-transparent border-t-blue-600
          animate-spin
        `}
      />
      <div
        className={`
          absolute inset-1 rounded-full border-2 border-transparent border-t-purple-600
          animate-spin-reverse
        `}
        style={{ animationDuration: '1.5s' }}
      />
    </div>
  );

  const variants = {
    spinner: <SpinnerLoader />,
    gradient: <GradientSpinner />,
    dots: <DotsLoader />,
    pulse: <PulseDotsLoader />,
    bars: <BarsLoader />,
    ring: <RingLoader />,
  };

  const loader = (
    <div className="flex flex-col items-center justify-center space-y-3">
      {variants[variant] || variants.spinner}
      {text && (
        <p className={`
          text-sm font-medium animate-pulse
          ${color === 'white' ? 'text-white' : darkMode ? 'text-gray-300' : 'text-gray-600'}
        `}>
          {text}
        </p>
      )}
    </div>
  );

  if (fullScreen) {
    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 animate-fade-in">
        <div className={`rounded-2xl p-8 shadow-2xl animate-bounce-in ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
          {loader}
        </div>
      </div>
    );
  }

  return loader;
};

/**
 * Skeleton Component
 * A loading skeleton component with shimmer effect
 */
const Skeleton = ({
  width = '100%',
  height = '1rem',
  circle = false,
  count = 1,
  animate = true,
  className = '',
}) => {
  const darkMode = useSelector(selectDarkMode);
  const skeletons = Array.from({ length: count });

  return (
    <>
      {skeletons.map((_, index) => (
        <div
          key={index}
          className={`
            relative overflow-hidden
            ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}
            ${circle ? 'rounded-full' : 'rounded-lg'}
            ${className}
          `}
          style={{ width, height }}
        >
          {animate && (
            <div
              className={`absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent ${
                darkMode ? 'via-gray-600/40' : 'via-white/40'
              } to-transparent animate-shimmer`}
            />
          )}
        </div>
      ))}
    </>
  );
};

/**
 * SkeletonCard Component
 * A skeleton loader for card-like content with shimmer
 */
const SkeletonCard = ({ animated = true }) => {
  const darkMode = useSelector(selectDarkMode);

  return (
    <div className={`rounded-xl shadow-sm border p-6 space-y-4 animate-fade-in ${
      darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-100'
    }`}>
      <div className="flex items-center space-x-4">
        <Skeleton circle width="3rem" height="3rem" animate={animated} />
        <div className="flex-1 space-y-2">
          <Skeleton width="60%" height="1rem" animate={animated} />
          <Skeleton width="40%" height="0.75rem" animate={animated} />
        </div>
      </div>
      <div className="space-y-2">
        <Skeleton width="100%" height="0.75rem" animate={animated} />
        <Skeleton width="90%" height="0.75rem" animate={animated} />
        <Skeleton width="80%" height="0.75rem" animate={animated} />
      </div>
    </div>
  );
};

/**
 * SkeletonTable Component
 * A skeleton loader for table content with shimmer
 */
const SkeletonTable = ({ rows = 5, columns = 4, animated = true }) => {
  return (
    <div className="space-y-4 animate-fade-in">
      {/* Header */}
      <div className="flex space-x-4">
        {Array.from({ length: columns }).map((_, index) => (
          <Skeleton key={index} width="100%" height="2.5rem" animate={animated} />
        ))}
      </div>
      {/* Rows */}
      {Array.from({ length: rows }).map((_, rowIndex) => (
        <div
          key={rowIndex}
          className="flex space-x-4"
          style={{ animationDelay: `${rowIndex * 50}ms` }}
        >
          {Array.from({ length: columns }).map((_, colIndex) => (
            <Skeleton
              key={colIndex}
              width="100%"
              height="3rem"
              animate={animated}
            />
          ))}
        </div>
      ))}
    </div>
  );
};

/**
 * TableLoadingSkeleton Component
 * A comprehensive skeleton loader for CRUD table pages with smooth animations
 */
const TableLoadingSkeleton = ({
  title = "items",
  rows = 5,
  columns = 4,
  showSearch = true,
  showFilters = true,
  showAddButton = true
}) => {
  const darkMode = useSelector(selectDarkMode);

  return (
    <div className="p-4 lg:p-6 animate-fade-in">
      {/* Search & Filter Skeleton */}
      {(showSearch || showFilters || showAddButton) && (
        <div className={`rounded-xl p-4 shadow-soft border mb-6 ${
          darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-100'
        }`}>
          <div className="flex flex-col md:flex-row gap-4">
            {showSearch && (
              <div className="flex-1">
                <Skeleton width="100%" height="2.75rem" animate={true} />
              </div>
            )}
            <div className="flex items-center gap-3">
              {showFilters && (
                <Skeleton width="140px" height="2.75rem" animate={true} />
              )}
              {showAddButton && (
                <Skeleton width="120px" height="2.75rem" animate={true} />
              )}
            </div>
          </div>
        </div>
      )}

      {/* Results Summary Skeleton */}
      <div className="my-6">
        <Skeleton width="200px" height="1rem" animate={true} />
      </div>

      {/* Table Skeleton */}
      <div className={`rounded-2xl shadow-soft border overflow-hidden ${
        darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-100'
      }`}>
        {/* Table Header */}
        <div className={`${darkMode ? 'bg-gray-900' : 'bg-[#e9f1ff]'}`}>
          <div className="flex px-6 py-4 gap-4">
            {Array.from({ length: columns }).map((_, index) => (
              <div
                key={index}
                className="flex-1"
                style={{ animationDelay: `${index * 50}ms` }}
              >
                <Skeleton width="80%" height="1rem" animate={true} />
              </div>
            ))}
            <div className="w-24">
              <Skeleton width="100%" height="1rem" animate={true} />
            </div>
          </div>
        </div>

        {/* Table Rows */}
        <div className={`divide-y ${darkMode ? 'divide-gray-700' : 'divide-gray-200'}`}>
          {Array.from({ length: rows }).map((_, rowIndex) => (
            <div
              key={rowIndex}
              className={`flex px-6 py-4 gap-4 transition-all duration-300 ${
                darkMode ? 'hover:bg-gray-700/30' : 'hover:bg-gray-50'
              }`}
              style={{
                animationDelay: `${rowIndex * 100}ms`,
                opacity: 1 - (rowIndex * 0.08)
              }}
            >
              {Array.from({ length: columns }).map((_, colIndex) => (
                <div
                  key={colIndex}
                  className="flex-1"
                  style={{ animationDelay: `${(rowIndex * columns + colIndex) * 30}ms` }}
                >
                  <Skeleton
                    width={colIndex === 0 ? "70%" : colIndex === columns - 1 ? "50%" : "60%"}
                    height="1.25rem"
                    animate={true}
                  />
                </div>
              ))}
              {/* Action Buttons Skeleton */}
              <div className="w-24 flex items-center justify-center gap-2">
                <Skeleton width="24px" height="24px" animate={true} className="rounded" />
                <Skeleton width="24px" height="24px" animate={true} className="rounded" />
                <Skeleton width="24px" height="24px" animate={true} className="rounded" />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Pagination Skeleton */}
      <div className="mt-6 flex items-center justify-between">
        <Skeleton width="80px" height="2.5rem" animate={true} />
        <div className="flex items-center gap-2">
          {Array.from({ length: 3 }).map((_, index) => (
            <Skeleton
              key={index}
              width="40px"
              height="40px"
              animate={true}
              className="rounded-xl"
            />
          ))}
        </div>
        <Skeleton width="80px" height="2.5rem" animate={true} />
      </div>
    </div>
  );
};

/**
 * TableBodySkeleton Component
 * A skeleton loader for table body during pagination loading
 */
const TableBodySkeleton = ({ rows = 5, columns = 4, hasExpandColumn = false }) => {
  const darkMode = useSelector(selectDarkMode);
  const totalCols = columns + 1 + (hasExpandColumn ? 1 : 0); // +1 for actions

  return (
    <>
      {Array.from({ length: rows }).map((_, rowIndex) => (
        <tr
          key={rowIndex}
          className={`animate-pulse ${darkMode ? 'bg-gray-800' : 'bg-white'}`}
          style={{ animationDelay: `${rowIndex * 50}ms` }}
        >
          {hasExpandColumn && (
            <td className="px-6 py-4 text-center">
              <div className={`w-6 h-6 rounded-full mx-auto ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`} />
            </td>
          )}
          {Array.from({ length: columns }).map((_, colIndex) => (
            <td key={colIndex} className="px-6 py-4">
              <div
                className={`h-4 rounded ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`}
                style={{
                  width: colIndex === 0 ? '70%' : colIndex === columns - 1 ? '50%' : '60%',
                }}
              >
                <div
                  className={`h-full w-full bg-gradient-to-r from-transparent ${
                    darkMode ? 'via-gray-600/40' : 'via-white/40'
                  } to-transparent animate-shimmer`}
                />
              </div>
            </td>
          ))}
          <td className="px-6 py-4">
            <div className="flex items-center justify-center gap-2">
              <div className={`w-8 h-8 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`} />
              <div className={`w-8 h-8 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`} />
              <div className={`w-8 h-8 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`} />
            </div>
          </td>
        </tr>
      ))}
    </>
  );
};

/**
 * DetailPageSkeleton Component
 * A skeleton loader for detail/view pages with form-like layout
 */
const DetailPageSkeleton = ({
  fieldsCount = 9,
  showImage = true,
  columns = 3
}) => {
  const darkMode = useSelector(selectDarkMode);

  return (
    <div className="p-5 sm:p-6 animate-fade-in">
      {/* Header Skeleton */}
      <div className={`rounded-xl p-4 shadow-sm border mb-5 sm:mb-6 ${
        darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-100'
      }`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            {/* Back button skeleton */}
            <Skeleton width="24px" height="24px" animate={true} className="rounded" />
            {/* Title skeleton */}
            <Skeleton width="200px" height="1.75rem" animate={true} />
          </div>
          {/* Action buttons skeleton */}
          <div className="flex items-center space-x-3">
            <Skeleton width="130px" height="2.5rem" animate={true} className="rounded-xl" />
            <Skeleton width="30px" height="30px" animate={true} className="rounded" />
          </div>
        </div>
      </div>

      {/* Content Card Skeleton */}
      <div className={`rounded-xl shadow-sm border ${
        darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-100'
      }`}>
        <div className="p-8">
          <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-${columns} gap-x-12 gap-y-8`}>
            {/* Profile image skeleton (if showImage is true) */}
            {showImage && (
              <div style={{ animationDelay: '0ms' }}>
                <Skeleton width="80px" height="0.875rem" animate={true} className="mb-2" />
                <Skeleton circle width="7rem" height="7rem" animate={true} />
              </div>
            )}

            {/* Field skeletons */}
            {Array.from({ length: fieldsCount }).map((_, index) => (
              <div
                key={index}
                style={{ animationDelay: `${(index + 1) * 50}ms` }}
              >
                {/* Label skeleton */}
                <Skeleton
                  width={`${60 + Math.random() * 20}%`}
                  height="0.875rem"
                  animate={true}
                  className="mb-2"
                />
                {/* Value skeleton */}
                <Skeleton
                  width={`${70 + Math.random() * 25}%`}
                  height="1.25rem"
                  animate={true}
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * ProgressLoader Component
 * A progress bar style loader
 */
const ProgressLoader = ({ progress = 0, showPercent = true, color = 'primary' }) => {
  const darkMode = useSelector(selectDarkMode);

  const colorClasses = {
    primary: 'bg-blue-600',
    success: 'bg-green-600',
    warning: 'bg-yellow-600',
    danger: 'bg-red-600',
    gradient: 'bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500',
  };

  return (
    <div className="w-full">
      <div className="flex justify-between mb-1">
        {showPercent && (
          <span className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            {Math.round(progress)}%
          </span>
        )}
      </div>
      <div className={`w-full rounded-full h-2.5 overflow-hidden ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
        <div
          className={`h-full rounded-full transition-all duration-500 ease-bounce-out ${colorClasses[color]}`}
          style={{ width: `${progress}%` }}
        />
      </div>
    </div>
  );
};

/**
 * PageLoader Component
 * Full page loader with logo
 */
const PageLoader = ({ text = 'Loading...' }) => {
  const darkMode = useSelector(selectDarkMode);

  return (
    <div className={`fixed inset-0 z-50 flex flex-col items-center justify-center animate-fade-in ${
      darkMode ? 'bg-gray-900' : 'bg-white'
    }`}>
      <div className="relative">
        <Loader variant="ring" size="xl" />
      </div>
      <p className={`mt-4 font-medium animate-pulse ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>{text}</p>
    </div>
  );
};

export { Loader, Skeleton, SkeletonCard, SkeletonTable, TableLoadingSkeleton, TableBodySkeleton, DetailPageSkeleton, ProgressLoader, PageLoader };
export default Loader;
